import { Authenticated, ErrorComponent, Refine } from "@refinedev/core";
import routerBindings, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { Layout } from "./components/layout";
import { OpenOnMobileModal } from "./components/layout/open-on-mobile-qr-code";
import { TraceInformation } from "./components/layout/tracing";
import { authProvider} from "./lib/authProvider";
import { notificationProvider } from "./lib/notification-provider";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

import { ForgotPassword } from "./pages/forgot-password";
import { PreviewCards } from "./pages/preview-cards/preview-cards";
import { ResetPassword } from "./pages/reset-password";
import { Login } from "./pages/login";
import { MemberView } from "./pages/members/user-view/user-view";
import { ProfileSettings } from "./pages/profile-settings/profile-settings";
import { apiResources } from "./resources";
import { dataProvider } from "./rest-data-provider";
import { QueryClient } from "@tanstack/react-query";
import { DEVICE_ID } from "./utils/deviceId";

import * as Sentry from "@sentry/react";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { getEnvironment } from "./utils/environment";

console.log("Initializing QueryClient...");
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: "offlineFirst",
      cacheTime: 1000 * 60 * 60 * 24 * 30, // 14 days
    },
  },
});

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

persistQueryClient({
  queryClient,
  persister: localStoragePersister,
});

const environment = getEnvironment();
console.log("Environment:", environment);

Sentry.init({
  dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
  environment: environment, // Set the environment
  enabled: environment && environment !== "development", // Disable for localhost/development
  integrations: [
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
    Sentry.httpClientIntegration({
      failedRequestStatusCodes: [
        400, 401, 403, 404, 422, 429, 500, 502, 503, 504,
      ],
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    import.meta.env.VITE_PUBLIC_BACKEND_API_URL,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Only add device ID to Sentry if it was created successfully
if (DEVICE_ID) {
  Sentry.setTag("device_id", DEVICE_ID);
  console.log("Device ID set in Sentry:", DEVICE_ID.substring(0, 8) + "...");
}

console.log("API_URL", import.meta.env.VITE_PUBLIC_BACKEND_API_URL);
function App() {
  const { t, i18n } = useTranslation();

  const i18nProvider = {
    translate: (key: string, params: object) => t(key, params),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };
  return (
    <BrowserRouter>
      {/*<GitHubBanner />*/}

      <OpenOnMobileModal />
      <TraceInformation />
      <Refine
        authProvider={authProvider}
        dataProvider={dataProvider(import.meta.env.VITE_PUBLIC_BACKEND_API_URL)}
        i18nProvider={i18nProvider}
        notificationProvider={notificationProvider}
        routerProvider={routerBindings}
        resources={apiResources}
        options={{
          reactQuery: {
            clientConfig: queryClient,
          },
          // syncWithLocation: true,
          warnWhenUnsavedChanges: true,
          projectId: "wlKwZd-XBUCvy-Y1nslv",
        }}
      >
        <ToastContainer />
        <Routes>
          <Route
            element={
              <Authenticated
                key={"authenticationComponent"}
                v3LegacyAuthProviderCompatible={false}
                loading={"Loading authentication..."}
              >
                <Layout>
                  {
                    <>
                      <Outlet />
                    </>
                  }
                </Layout>
              </Authenticated>
            }
          >
            <Route path="/" loader={true} element={<MemberView />}></Route>
            <Route
              path="/profile-settings"
              element={<ProfileSettings />}
            ></Route>
            <Route path="/preview-cards" element={<PreviewCards />}></Route>
          </Route>
          <Route path="*" element={<ErrorComponent />} />
          <Route>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/" element={<ResetPassword />} />
          </Route>
        </Routes>
        <UnsavedChangesNotifier />
        <DocumentTitleHandler handler={() => "CPG Card"} />
      </Refine>
    </BrowserRouter>
  );
}

export default App;
